import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { MLInput } from 'components/Input/Input';
import { useCreateOwnerForClient } from 'hooks/Clients/smartUsersForClientCreate/useCreateOwnerForClient';

interface CreateOwnerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateOwnerModal = ({ isOpen, onClose }: CreateOwnerModalProps) => {
  const { createOwner, isLoading: isLoadingOwner } = useCreateOwnerForClient();
  const { t } = useTranslation();

  const {
    handleSubmit: handleOwnerSubmit,
    control: ownerControl,
    reset: resetOwner,
    setError: setOwnerError,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      confirm_password: '',
      send_email_notifications: false,
    },
  });

  const onCloseModal = () => {
    onClose();
    resetOwner();
  };

  const onCreateOwner = (ownerData: any) => {
    createOwner(ownerData, {
      onSuccess: () => {
        enqueueSnackbar(t('Modals.manage-owner.successCreate'), { variant: 'success' });
        onCloseModal();
      },
      onError: (error: any) => {
        handleOwnerError(error);
        enqueueSnackbar(t('Modals.manage-owner.failCreate'), { variant: 'error' });
      },
    });
  };

  const handleOwnerError = (error: any) => {
    if (error.response?.data) {
      const errorData = error.response.data;

      // Handle field-specific errors
      if (errorData.first_name) {
        setOwnerError('first_name', { message: errorData.first_name[0] });
      }
      if (errorData.last_name) {
        setOwnerError('last_name', { message: errorData.last_name[0] });
      }
      if (errorData.email) {
        setOwnerError('email', { message: errorData.email[0] });
      }
      if (errorData.phone) {
        setOwnerError('phone', { message: errorData.phone[0] });
      }
      if (errorData.password) {
        setOwnerError('password', { message: errorData.password[0] });
      }
      if (errorData.confirm_password) {
        setOwnerError('confirm_password', { message: errorData.confirm_password[0] });
      }

      // Handle non-field errors (like password mismatch)
      if (errorData.non_field_errors) {
        setOwnerError('confirm_password', { message: errorData.non_field_errors[0] });
        setOwnerError('password', { message: errorData.non_field_errors[0] });
      }
    } else {
      enqueueSnackbar(t('Modals.manage-owner.failCreate'), { variant: 'error' });
    }
  };

  return (
    <div>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          // onReset();
          onClose();
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader
          className="border-bottom py-3"
          toggle={() => {
            // onReset();
            onClose();
          }}
        >
          <div className="d-flex justify-content-between align-items-center w-100 gap-5">
            <div className="d-flex justify-content-between align-items-center w-100 gap-5">
              <div className="text-purple">{t('Modals.manage-owner.createOwnerHeader')}</div>

              <Button className="btn btn-ghost-purple btn-sm" onClick={() => onCloseModal()}>
                {t('Modals.manage-client.backToClientCreation')}
              </Button>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-12">
              <h5 className="mb-1">{t('Modals.manage-owner.title')}</h5>
              <small className="text-muted d-block mb-2" style={{ lineHeight: '1.2', fontSize: '0.85em' }}>
                The primary contact responsible for this account.
              </small>
              <div className="row mb-3 ">
                <div className="col-md-6">
                  <Controller
                    control={ownerControl}
                    name="first_name"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput {...field} label={t('Modals.manage-owner.firstName')} error={error?.message} required />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={ownerControl}
                    name="last_name"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput {...field} label={t('Modals.manage-owner.lastName')} error={error?.message} required />
                    )}
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <Controller
                    control={ownerControl}
                    name="email"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-owner.email')}
                        type="email"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                  <Controller
                    control={ownerControl}
                    name="send_email_notifications"
                    render={({ field }) => (
                      <>
                        <div className="form-check mt-1">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="smstReceiveMessagesCheckbox"
                            checked={field.value}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                          <Label className="form-check-label mb-0 text-muted" htmlFor="smstReceiveMessagesCheckbox">
                            Send account credentials via email
                          </Label>
                        </div>
                      </>
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={ownerControl}
                    name="phone"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput {...field} label={t('Modals.manage-owner.phone')} type="phone" error={error?.message} />
                    )}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <Controller
                    control={ownerControl}
                    name="password"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-owner.password')}
                        type="password"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={ownerControl}
                    name="confirm_password"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-owner.confirmPassword')}
                        type="password"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-ghost-danger" onClick={onCloseModal}>
            {t('Modals.delete-modal.cancel')}
          </Button>
          <Button
            className="btn btn-ghost-success"
            onClick={handleOwnerSubmit(onCreateOwner)}
            disabled={isLoadingOwner}
          >
            {isLoadingOwner ? t('Modals.manage-client.creating') : t('Modals.manage-owner.createOwner')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CreateOwnerModal;
